import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { AbstractSecurityStorage, LogLevel, provideAuth } from 'angular-auth-oidc-client';

import { keycloakConfig } from './core/config/auth.config';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { AuthService } from './core/services/auth.service';
import { SplitSecurityStorage } from './core/services/split-security-storage';
import { routes } from './app.routes';
import { provideGtNgDatepicker } from './shared/gt-ng-datepicker/gt-ng-datepicker.providers';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Order matters, outermost first.
    // - authInterceptor stays first so it still owns 401: it resolves/refreshes the token and
    //   runs the session-expiry hand-off, and errorInterceptor defers to it rather than
    //   toasting over the "เซสชันหมดอายุ" dialog.
    // - errorInterceptor is innermost so it sees the raw server error before anything else
    //   maps or swallows it.
    // - the loading counter therefore starts only *after* the token resolves. That is
    //   intended: a request queued behind a token refresh has not hit the network yet.
    provideHttpClient(withInterceptors([authInterceptor, loadingInterceptor, errorInterceptor])),
    // Keycloak through angular-auth-oidc-client. Deliberately NOT registered: the library's own
    // `authInterceptor()` / `secureRoutes` — `core/interceptors/auth.interceptor.ts` keeps
    // attaching the bearer token (or none, for anonymous visitors) and owning 401 → session expiry.
    provideAuth({
      config: {
        authority: `${keycloakConfig.url}/realms/${keycloakConfig.realm}`,
        clientId: keycloakConfig.clientId,
        // The code is always exchanged against this URL (the origin root); the app's destination
        // after the round-trip is stored in sessionStorage — see AuthService.login()/initialize().
        redirectUrl: keycloakConfig.redirectUri,
        postLogoutRedirectUri: keycloakConfig.postLogoutRedirectUri,
        scope: keycloakConfig.scope,
        // Keycloak's login page opens in Thai whatever the browser's Accept-Language says (issue
        // #204): `ui_locales` outranks Accept-Language. The page's language switcher still wins
        // for the rest of that login (kc_locale → KEYCLOAK_LOCALE cookie); the next login starts
        // in Thai again. Merged by the library into every authorize(), customParams included.
        customParamsAuthRequest: { ui_locales: 'th' },
        responseType: 'code', // authorization code; PKCE S256 is automatic for this flow
        silentRenew: true,
        useRefreshToken: true, // refresh-token grant: no silent-renew iframe anywhere
        // Keycloak issues refresh tokens without `offline_access` (which there means *offline*
        // tokens — not wanted); this only silences the library's boot-time warning about it.
        disableRefreshTokenOfflineAccessScopeWarning: true,
        renewTimeBeforeTokenExpiresInSeconds: 30, // mirrors the old updateToken(30)
        ignoreNonceAfterRefresh: true,
        triggerRefreshWhenIdTokenExpired: false,
        autoUserInfo: true, // userinfo replaces keycloak.loadUserProfile()
        // The app owns post-login navigation (initialize() restores the parked destination), so
        // the library must not navigateByUrl(postLoginRoute / unauthorizedRoute — both '/') on
        // its own while it processes the callback: the app restores the intended destination
        // after login (normally /home, with explicit destinations for special round-trips).
        triggerAuthorizationResultEvent: true,
        logLevel: LogLevel.Warn,
      },
    }),
    // Tokens stay in memory; only the login round-trip state touches sessionStorage. Must come
    // after provideAuth(), which registers the library's default sessionStorage-backed storage.
    { provide: AbstractSecurityStorage, useClass: SplitSecurityStorage },
    provideAppInitializer(() => inject(AuthService).initialize()),
    provideRouter(routes),
    ...provideGtNgDatepicker(),
  ],
};
